import { useState } from 'react';
import { Search, Cpu, Sparkles, Loader2, BookOpenCheck } from 'lucide-react';
import { useCadStore } from '../store/cadStore';
import { useThemeTokens } from '../theme';
import { geometryEngine } from '../services/geometryEngine';
import { amplify } from '../services/learningService';
import { useScrapeStore } from '../store/scrapeStore';

const QUICK_PROMPTS = [
    'Simple rocket on a launch pad',
    'Round gear with teeth and a hub hole',
    'Solid coffee mug with a handle',
    'Four-legged table with a thin top',
    'Desk robot with head, arms and wheels',
    'Vase with a flared rim',
    'Wrench with an open-ended head',
    'Bolt with a hexagonal head',
];

/* AI Command Bar — standard generative prompts, boosted by the account's
   Vectorize knowledge fetched via local Ollama embeddings. */
export default function AICommandBar({ onClose }) {
    const t = useThemeTokens();
    const prompt = useCadStore((s) => s.prompt);
    const setPrompt = useCadStore((s) => s.setPrompt);
    const isResearching = useCadStore((s) => s.isResearching);
    const activeProjectId = useCadStore((s) => s.activeProjectId);
    const aiStatus = useCadStore((s) => s.aiStatus);
    const amplifiedMatches = useScrapeStore((s) => s.amplified.matches.length);
    const [error, setError] = useState('');

    const generate = async (text) => {
        const input = (text || prompt).trim();
        if (!input) return;
        setError('');
        const st = useCadStore.getState();
        st.setPrompt(input);
        st.setResearching(true); st.setAiStatus('researching');
        try {
            const context = await amplify(input, activeProjectId);   // Vectorize boost
            const geometry = await geometryEngine.generateFromPrompt(input, context);
            if (!Array.isArray(geometry) || geometry.length === 0) throw new Error('Empty result.');
            const pb = st.settings.printBed || { x: 220, y: 220, z: 250 };
            const centered = geometryEngine.centerAndFit(geometry, pb);
            const parts = centered.map((p) => ({ ...p, id: Math.random().toString(36).slice(2, 11), color: p.color || '#3b82f6' }));
            st.setCurrentModel(parts); st.setSelectedPart(null); st.setAiStatus('completed');
            window.__roocad_fitView?.();
        } catch (err) {
            console.error('Command bar generation failed:', err);
            setError(err.message || 'Generation failed');
            st.setAiStatus('error');
        } finally {
            st.setResearching(false);
        }
    };
return (
        <div style={{
            position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)', zIndex: 25,
            width: 'min(680px, 92%)', background: t.surface, border: `1px solid ${t.borderLight}`,
            borderRadius: 14, boxShadow: t.shadow, padding: 10, display: 'flex', flexDirection: 'column', gap: 8,
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Sparkles size={16} color={t.accent} style={{ flexShrink: 0 }} />
                <input
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') generate(); }}
                    placeholder="Describe a part to build — e.g. “aerodome housing with 6 mounting tabs…”"
                    style={{
                        flex: 1, padding: '9px 12px', background: t.inputBg, border: `1px solid ${t.borderLight}`,
                        borderRadius: 9, color: t.text, fontSize: 13, outline: 'none', fontFamily: 'inherit',
                    }}
                />
                <button onClick={() => generate()} disabled={isResearching || !prompt.trim()}
                    style={{
                        display: 'flex', alignItems: 'center', gap: 7, padding: '9px 16px', borderRadius: 9, cursor: 'pointer',
                        border: 'none', fontWeight: 700, fontSize: 12.5, color: '#fff',
                        background: 'linear-gradient(135deg,#2563eb,#06b6d4)', opacity: isResearching || !prompt.trim() ? 0.5 : 1,
                    }}>
                    {isResearching ? <Loader2 size={15} className="animate-spin" /> : <Search size={15} />}
                    {isResearching ? 'Building…' : 'Build'}
                </button>
                {onClose && (
                    <button onClick={onClose} title="Dismiss" style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: t.textMuted, padding: 6 }}>✕</button>
                )}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, alignItems: 'center' }}>
                {QUICK_PROMPTS.map((p) => (
                    <button key={p} onClick={() => generate(p)} title={`Build: ${p}`}
                        style={{
                            padding: '4px 9px', borderRadius: 999, border: `1px solid ${t.borderLight}`, cursor: 'pointer',
                            background: t.surface, color: t.textSub, fontSize: 11, fontWeight: 600,
                        }}>
                        {p}
                    </button>
                ))}
                {amplifiedMatches > 0 && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginLeft: 'auto', fontSize: 10.5, color: t.success, fontWeight: 600 }}>
                        <BookOpenCheck size={12} /> {amplifiedMatches} learned ref{amplifiedMatches === 1 ? '' : 's'} applied
                    </span>
                )}
            </div>
            {error && <span style={{ fontSize: 11, color: t.danger, fontWeight: 600 }}>{error}</span>}
            {aiStatus === 'researching' && <span style={{ fontSize: 11, color: t.textMuted, display: 'inline-flex', alignItems: 'center', gap: 6 }}><Cpu size={12} className="animate-spin" /> Consulting Ollama + Vectorize…</span>}
        </div>
    );
}